/* ==========================================================================
   Markdown Text
   Renders the small Markdown subset used for pharmacy-authored copy
   (PORTAL-159) — headings, bullet/numbered lists, paragraphs, and inline
   bold/italic/code/links.

   Like the changelog renderer this returns React nodes rather than an HTML
   string, so authored text is escaped as usual and can never be interpreted
   as markup. That leaves link targets as the only injection vector, and
   `safeHref` allowlists those. Unmatched or unsupported syntax simply renders
   as written.
   ========================================================================== */

import type { ReactNode } from 'react'

/* --------------------------------------------------------------------------
   Inline markup
   -------------------------------------------------------------------------- */

/**
 * Matches `***bold italic***`, `**bold**`, `*italic*`, `` `code` `` or
 * `[text](url)`.
 *
 * Italic requires a non-space character just inside each `*`, as CommonMark
 * does, so asterisks used as footnote markers or multiplication signs
 * (`Schedule II* and III*`, `1 capsule * 2 daily`) are left alone.
 */
const INLINE_MARKUP =
  /\*\*\*([^*\n]+?)\*\*\*|\*\*([\s\S]+?)\*\*|\*([^\s*](?:[^*\n]*?[^\s*])?)\*|`([^`]+?)`|\[([^\]\n]+)\]\(([^)\s]+)\)/g

/**
 * Allowlists link targets. Only absolute http(s) and mailto URLs are followed;
 * anything else (notably `javascript:`) returns null so the link renders as
 * plain text instead.
 */
function safeHref(url: string): string | null {
  const trimmed = url.trim()
  return /^(https?:\/\/|mailto:)/i.test(trimmed) ? trimmed : null
}

/**
 * Renders one line's inline markup. Bold, italic and link text are rendered
 * recursively so formats combine (`**[site](https://…)**`, `[**bold**](…)`);
 * the captured text is always shorter than its match, so this terminates.
 * Code stays literal.
 */
function renderInlineMarkup(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let cursor = 0

  for (const match of text.matchAll(INLINE_MARKUP)) {
    const [raw, boldItalic, bold, italic, code, linkText, linkUrl] = match
    const start = match.index ?? 0
    const key = `${keyPrefix}-${start}`

    if (start > cursor) nodes.push(text.slice(cursor, start))

    if (boldItalic !== undefined) {
      nodes.push(
        <strong key={key} className="font-semibold">
          <em>{renderInlineMarkup(boldItalic, `${key}-bi`)}</em>
        </strong>
      )
    } else if (bold !== undefined) {
      nodes.push(
        <strong key={key} className="font-semibold">
          {renderInlineMarkup(bold, `${key}-b`)}
        </strong>
      )
    } else if (italic !== undefined) {
      nodes.push(<em key={key}>{renderInlineMarkup(italic, `${key}-i`)}</em>)
    } else if (code !== undefined) {
      nodes.push(
        <code
          key={key}
          className="font-mono text-[0.9em] bg-gray-100 rounded px-1 py-0.5"
        >
          {code}
        </code>
      )
    } else {
      const href = linkUrl ? safeHref(linkUrl) : null
      nodes.push(
        href ? (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--pharmacy-primary)] underline hover:no-underline"
          >
            {renderInlineMarkup(linkText, `${key}-l`)}
          </a>
        ) : (
          // Unsupported scheme — show what was written rather than link to it.
          <span key={key}>{raw}</span>
        )
      )
    }

    cursor = start + raw.length
  }

  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

/** Renders one line's inline markup, turning newlines into line breaks. */
function renderLines(text: string, keyPrefix: string): ReactNode[] {
  return text.split('\n').flatMap((line, i) => [
    ...(i > 0 ? [<br key={`${keyPrefix}-br-${i}`} />] : []),
    ...renderInlineMarkup(line, `${keyPrefix}-${i}`),
  ])
}

/* --------------------------------------------------------------------------
   Block parsing
   -------------------------------------------------------------------------- */

type Block =
  | { type: 'heading'; level: 2 | 3; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; start: number; items: string[] }
  | { type: 'p'; text: string }

const HEADING = /^(#{1,6})\s+(.*)$/
const BULLET = /^\s*[-*]\s+(.*)$/
// The number is kept so a list resumed after a paragraph or sub-bullets
// continues from where the author left it instead of restarting at 1.
const NUMBERED = /^\s*(\d{1,9})[.)]\s+(.*)$/

function parseBlocks(source: string): Block[] {
  const blocks: Block[] = []
  let paragraph: string[] = []

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'p', text: paragraph.join('\n') })
      paragraph = []
    }
  }

  for (const line of source.replace(/\r\n?/g, '\n').split('\n')) {
    const heading = HEADING.exec(line)
    const bullet = BULLET.exec(line)
    const numbered = NUMBERED.exec(line)

    if (!line.trim()) {
      flushParagraph()
    } else if (heading) {
      flushParagraph()
      blocks.push({
        type: 'heading',
        // Everything above `###` collapses into the two sizes we style.
        level: heading[1].length <= 2 ? 2 : 3,
        text: heading[2],
      })
    } else if (bullet) {
      flushParagraph()
      const last = blocks[blocks.length - 1]
      if (last?.type === 'ul') last.items.push(bullet[1])
      else blocks.push({ type: 'ul', items: [bullet[1]] })
    } else if (numbered) {
      flushParagraph()
      const last = blocks[blocks.length - 1]
      if (last?.type === 'ol') last.items.push(numbered[2])
      else blocks.push({ type: 'ol', start: Number(numbered[1]), items: [numbered[2]] })
    } else {
      paragraph.push(line)
    }
  }

  flushParagraph()
  return blocks
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export interface MarkdownTextProps {
  /** Markdown-subset source text. */
  text: string
  /** Extra classes for the wrapper. */
  className?: string
}

/**
 * Renders pharmacy-authored Markdown as React nodes.
 *
 * @example
 * ```tsx
 * <MarkdownText text={"## Rules\n\n- Fax by **4pm**"} />
 * ```
 */
export function MarkdownText({ text, className = '' }: MarkdownTextProps) {
  const blocks = parseBlocks(text)
  if (!blocks.length) return null

  return (
    <div className={`space-y-3 text-charcoal ${className}`.trim()}>
      {blocks.map((block, i) => {
        const key = `block-${i}`

        if (block.type === 'heading') {
          return block.level === 2 ? (
            <h3 key={key} className="font-serif text-lg text-charcoal">
              {renderInlineMarkup(block.text, key)}
            </h3>
          ) : (
            <h4 key={key} className="font-medium text-charcoal">
              {renderInlineMarkup(block.text, key)}
            </h4>
          )
        }

        if (block.type === 'ul') {
          return (
            <ul key={key} className="list-disc list-inside space-y-1.5">
              {block.items.map((item, j) => (
                <li key={j}>{renderInlineMarkup(item, `${key}-${j}`)}</li>
              ))}
            </ul>
          )
        }

        if (block.type === 'ol') {
          return (
            <ol key={key} start={block.start} className="list-decimal list-inside space-y-1.5">
              {block.items.map((item, j) => (
                <li key={j}>{renderInlineMarkup(item, `${key}-${j}`)}</li>
              ))}
            </ol>
          )
        }

        return (
          <p key={key} className="leading-relaxed">
            {renderLines(block.text, key)}
          </p>
        )
      })}
    </div>
  )
}

export default MarkdownText
